import { CloseOutlined, UserAddOutlined, UserOutlined } from "@ant-design/icons"
import { AvatarSelect } from "components/AvatarSelect/AvatarSelect"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { Form } from "components/Form/Form"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { Select } from "components/Select/Select"
import { useDisclosure } from "hooks/useDisclosure"
import { Escalation } from "interface/escalation"
import { useMemo } from "react"
import {
	useAssignEscalation,
	useEscalationCandidates,
	useUpdateEscalationObservers,
} from "services/escalations"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"

export function EscalationAssigneeObservers({
	escalation,
}: {
	escalation: Escalation
}) {
	return (
		<>
			<AddObservers escalation={escalation} />

			<AssignEscalation escalation={escalation} />
		</>
	)
}

function AddObservers({
	escalation: { id, observers, patientId },
}: {
	escalation: Escalation
}) {
	const { notify } = useNotification()
	const { options, isLoading } = useCandidateOptions(patientId)
	const value = useMemo(() => observers.map(({ id }) => id), [observers])
	const update = useUpdateEscalationObservers(id)
	const handleAdd = (observerIds: string[]) => {
		update.mutate(
			{ observerIds },
			{
				onError: ({ message }) =>
					notify.error({ message: message || GENERIC_ERROR_MESSAGE }),
			},
		)
	}

	return (
		<AvatarSelect
			value={value}
			options={options}
			aria-label="Observers"
			placeholder="Select observers"
			buttonProps={{ loading: update.isPending }}
			loading={isLoading || update.isPending}
			onChange={handleAdd}
		/>
	)
}

function AssignEscalation({
	escalation: { id, assignee, assigneeName, observers, patientId },
}: {
	escalation: Escalation
}) {
	const { options, isLoading } = useCandidateOptions(patientId)
	const observerIds = useMemo(() => observers.map(({ id }) => id), [observers])
	const assign = useAssignEscalation(id)
	const update = useUpdateEscalationObservers(id)
	const editing = useDisclosure<Escalation["assignee"]>({
		defaultValue: assignee,
		onClose: () => {
			assign.reset()
			update.reset()
		},
	})
	const assigneeId = editing.value
	const isAssigned = Boolean(assigneeId)
	const Icon = isAssigned ? UserOutlined : UserAddOutlined
	const label = assigneeName || "Assign escalation"
	const handleAdd = (observerIds: string[]) => {
		update.mutate({ observerIds })
	}
	const handleAssign = () => {
		if (assigneeId) {
			assign.mutate({ assigneeId }, { onSuccess: editing.close })
		}
	}

	return (
		<>
			<Button
				icon={<Icon aria-hidden />}
				onClick={() => editing.open(assignee)}
			>
				{label}
			</Button>

			{editing.isOpen && (
				<Modal
					open
					cancelText="Cancel"
					title="Assign escalation"
					okText="Assign escalation"
					okButtonProps={{ disabled: !assigneeId || assignee === assigneeId }}
					confirmLoading={assign.isPending}
					classNames={{ body: "flex flex-col gap-2" }}
					onCancel={editing.close}
					onOk={handleAssign}
				>
					<fieldset disabled={assign.isPending || update.isPending}>
						<Form.Item
							layout="vertical"
							className="mb-0"
							validateStatus={assign.isError ? "error" : undefined}
							help={assign.error?.message}
							label="Once assigned, the assignee will be notified about this
								escalation and will receive reminders (after 24h and 48h) until
								they open the escalation."
						>
							<Select
								virtual={false}
								options={options}
								value={assigneeId}
								className="w-full"
								aria-label="Select assignee"
								placeholder="Select assignee"
								loading={isLoading || assign.isPending}
								onChange={(value) => {
									assign.reset()
									editing.open(value)
								}}
							/>
						</Form.Item>

						<Divider className="mt-4 mb-2" />

						<Form.Item
							layout="vertical"
							className="mb-0"
							validateStatus={update.isError ? "error" : undefined}
							help={update.error?.message}
							label="Add as observer"
						>
							<Select<string[]>
								allowClear
								mode="multiple"
								virtual={false}
								options={options}
								className="w-full"
								value={observerIds}
								aria-label="Observers"
								maxTagCount="responsive"
								placeholder="Select observers"
								loading={isLoading || update.isPending}
								showSearch={{
									optionFilterProp: "label",
									autoClearSearchValue: false,
								}}
								removeIcon={
									<CloseOutlined role="button" aria-label="Remove observer" />
								}
								onChange={handleAdd}
							/>
						</Form.Item>
					</fieldset>
				</Modal>
			)}
		</>
	)
}

function useCandidateOptions(patientId: Escalation["patientId"]) {
	const { data: candidates = [], isLoading } =
		useEscalationCandidates(patientId)
	const options = useMemo(
		() =>
			candidates.map((provider) => ({
				label: provider.fullName,
				value: provider.id,
			})),
		[candidates],
	)
	return { options, isLoading, candidates }
}
